import { useState } from "react";
import { motion } from "framer-motion";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Paper from "@mui/material/Paper";
import Link from "@mui/material/Link";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";

import axios from "axios";

function Login() {
  const navigate = useNavigate();

  // Yellow theme colors
  const yellowPrimary = "#FFEB3B"; // yellow[500]
  const yellowSecondary = "#FDD835"; // yellow[600]
  const yellowDark = "#F57F17"; // yellow[900]
  const yellowLight = "#FFF9C4"; // yellow[100]

  const [userForm, setUserForm] = useState({
    id: "",
    pass: "",
  });

  const [loginMessage, setLoginMessage] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    console.log("User form submitted", userForm);

    try {
      const response = await axios.post("/server/baos_function/api/login", {
        userID: userForm.id,
        password: userForm.pass,
      });

      console.log("Login", response.data);
      const role = response.data.type;

      if (response.data.success) {
        setLoginSuccess(true);
        setLoginMessage("Login successful!");
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.data));
        localStorage.setItem("type", role);

        setTimeout(() => {
          if (role === "screen") navigate("/screen");
          else if (role === "admin") navigate("/dashboard");
          else navigate("/addRate");
        }, 1000); // delay to show message
      } else {
        setLoginSuccess(false);
        setLoginMessage(response.data.message || "Login failed. Please try again.");
      }
    } catch (error) {
      setLoginSuccess(false);
      setLoginMessage("An error occurred. Please try again.");
      console.error("Login error", error.response?.data || error.message);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        {/* Logo and Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Paper
            elevation={4}
            sx={{
              width: 80,
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${yellowPrimary} 0%, ${yellowSecondary} 50%, ${yellowDark} 100%)`,
              mb: 2,
              mx: "auto",
            }}
          >
            <MonetizationOnOutlinedIcon sx={{ fontSize: 40, color: "white" }} />
          </Paper>

          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            color="text.primary"
          >
            BOAS Gold Rate Manager
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Access your premium Application
          </Typography>
        </Box>

        {/* Login Container */}
        <Card
          elevation={5}
          sx={{
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${yellowLight}`,
          }}
        >
          <motion.div
            key="user-form"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
                Sign in to your account
              </Typography>

              <form onSubmit={handleUserSubmit}>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="User ID"
                    name="id"
                    value={userForm.id}
                    onChange={handleInputChange}
                    placeholder="your-user-id"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKeyOutlinedIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: yellowSecondary,
                        },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: yellowDark,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Password"
                    name="pass"
                    value={userForm.pass}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    type="password"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: yellowSecondary,
                        },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: yellowDark,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                ></Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  sx={{
                    bgcolor: yellowSecondary,
                    color: "rgba(0, 0, 0, 0.87)",
                    "&:hover": {
                      bgcolor: yellowDark,
                      color: "white",
                    },
                    py: 1.5,
                  }}
                >
                  Sign In
                </Button>

                {loginMessage && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      textAlign: "center",
                      color: loginSuccess ? "green" : "red",
                    }}
                  >
                    {loginMessage}
                  </Typography>
                )}
              </form>
            </CardContent>
          </motion.div>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            &copy; {new Date().getFullYear()} Gold Rate Management System. All
            rights reserved.
          </Typography>
          <Box
            sx={{ mt: 1, display: "flex", justifyContent: "center", gap: 3 }}
          >
            <Link
              href="#"
              variant="body2"
              underline="hover"
              color="text.secondary"
              sx={{ "&:hover": { color: yellowDark } }}
            >
              Privacy Policy
            </Link>
            <Link
              href="#"
              variant="body2"
              underline="hover"
              color="text.secondary"
              sx={{ "&:hover": { color: yellowDark } }}
            >
              Terms of Service
            </Link>
            <Link
              href="#"
              variant="body2"
              underline="hover"
              color="text.secondary"
              sx={{ "&:hover": { color: yellowDark } }}
            >
              Help Center
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;
